import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosClient, apiClient } from '../../auth-lib';
import AdminLayout from '../components/AdminLayout';
import { Button, Card, StatusPill, Table, Skeleton, useToast } from '../../design-system';
import { formatServiceName, describeDueRule } from '../../lib/serviceCatalog';
import '../styles/OrganizationDetail.css';

function OrganizationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // This org's own custom services (organizationId === this org, never the
  // global catalog) — separate load since it lives in the hrms backend, not
  // the auth-service the rest of this page reads from.
  const [customServices, setCustomServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    fetchOrgDetails();
    fetchCustomServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOrgDetails = async () => {
    try {
      setLoading(true);
      const [orgRes, usersRes] = await Promise.all([
        axiosClient.get(`/organizations/${id}`),
        axiosClient.get(`/organizations/${id}/users`),
      ]);
      setOrg(orgRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to load organization';
      setError(message);
      toast.danger(message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomServices = async () => {
    try {
      setServicesLoading(true);
      const res = await apiClient.get(`/api/services/org/${id}/custom`);
      setCustomServices(res.data);
    } catch (err) {
      // Non-fatal — the org's core details above still render either way.
      toast.danger(err.response?.data?.error || 'Failed to load custom services');
    } finally {
      setServicesLoading(false);
    }
  };

  // Disabling here sets the service's own isActive flag — the same
  // enforcement every other consumer (this org's own Services page, task
  // creation, client enrollment) already filters on — so a disabled custom
  // service disappears from this organization entirely, not just from this list.
  const toggleCustomService = async (service, isActive) => {
    if (!isActive && !window.confirm(`Disable "${formatServiceName(service.name)}"? It will disappear from ${org.name} immediately — only you will still be able to see and re-enable it.`)) {
      return;
    }
    setTogglingId(service._id);
    try {
      await apiClient.put(`/api/services/org/${id}/${service._id}/active`, { isActive });
      setCustomServices((prev) =>
        prev.map((s) => (s._id === service._id ? { ...s, isActive } : s))
      );
      toast.success(`${formatServiceName(service.name)} ${isActive ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update service');
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Organization Details" subtitle="">
        <Card><Skeleton.Text lines={5} /></Card>
      </AdminLayout>
    );
  }

  if (error || !org) {
    return (
      <AdminLayout title="Organization Details" subtitle="">
        <div className="form-error" role="alert">{error || 'Organization not found'}</div>
      </AdminLayout>
    );
  }

  const userColumns = [
    { key: 'email', header: 'Email', render: (u) => u.email },
    { key: 'name', header: 'Name', render: (u) => u.name },
    { key: 'role', header: 'Role', render: (u) => <StatusPill variant="neutral" label={u.role.name} /> },
    { key: 'status', header: 'Status', render: (u) => <StatusPill status={u.isActive ? 'active' : 'inactive'} /> },
    { key: 'joined', header: 'Joined', render: (u) => new Date(u.createdAt).toLocaleDateString() },
  ];

  return (
    <AdminLayout title={org.name} subtitle="Organization details">
      <div className="org-detail-page">
        <div className="org-detail-header">
          <StatusPill status={org.subscription.status} />
          <Button variant="primary" onClick={() => navigate(`/admin/organizations/${org._id}/edit`)}>
            Edit Organization
          </Button>
        </div>

        <div className="detail-grid">
          <Card>
            <Card.Header title="Organization Details" />
            <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{org.name}</span></div>
            <div className="detail-row"><span className="detail-label">Slug</span><span className="detail-value">{org.slug}</span></div>
            <div className="detail-row"><span className="detail-label">Industry</span><span className="detail-value">{org.industry || '-'}</span></div>
            <div className="detail-row"><span className="detail-label">Description</span><span className="detail-value">{org.description || '-'}</span></div>
          </Card>

          <Card>
            <Card.Header title="Contact Information" />
            <div className="detail-row"><span className="detail-label">Email</span><span className="detail-value">{org.contactEmail || '-'}</span></div>
            <div className="detail-row"><span className="detail-label">Phone</span><span className="detail-value">{org.contactPhone || '-'}</span></div>
          </Card>

          <Card>
            <Card.Header title="Subscription" />
            <div className="detail-row"><span className="detail-label">Plan</span><StatusPill variant="info" label={org.subscription.plan} /></div>
            <div className="detail-row"><span className="detail-label">Status</span><StatusPill status={org.subscription.status} /></div>
            {org.subscription.expiresAt && (
              <div className="detail-row"><span className="detail-label">Expires</span><span className="detail-value">{new Date(org.subscription.expiresAt).toLocaleDateString()}</span></div>
            )}
          </Card>

          <Card>
            <Card.Header title="User Count" />
            <div className="stat-badge-wrap">
              <span className="stat-badge">{users.length}</span>
              <span className="detail-label">Total Users</span>
            </div>
          </Card>
        </div>

        <Card>
          <Card.Header title={`Organization Users (${users.length})`} />
          {users.length === 0 ? (
            <p className="empty-message">No users in this organization</p>
          ) : (
            <Table columns={userColumns} data={users} keyField="_id" />
          )}
        </Card>

        <Card>
          <Card.Header
            title={`Custom Services${customServices.length ? ` (${customServices.length})` : ''}`}
          />
          {servicesLoading ? (
            <Skeleton.Text lines={2} />
          ) : customServices.length === 0 ? (
            <p className="empty-message">This organization hasn't created any custom services of its own.</p>
          ) : (
            <div className="org-custom-services">
              {customServices.map((s) => (
                <div key={s._id} className={`org-custom-service-row ${s.isActive ? '' : 'org-custom-service-row-disabled'}`}>
                  <div className="org-custom-service-info">
                    <span className="org-custom-service-name">{formatServiceName(s.name)}</span>
                    <span className="org-custom-service-group">{s.groupName}</span>
                  </div>
                  <span className="org-custom-service-due">{describeDueRule(s.defaultDueRule)}</span>
                  <StatusPill status={s.isActive ? 'active' : 'inactive'} label={s.isActive ? 'Enabled' : 'Disabled'} />
                  <Button
                    variant={s.isActive ? 'danger' : 'secondary'}
                    size="sm"
                    loading={togglingId === s._id}
                    onClick={() => toggleCustomService(s, !s.isActive)}
                  >
                    {s.isActive ? 'Disable' : 'Enable'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <Card.Header title="Settings" />
          <div className="settings-grid">
            <div className="setting-item"><span className="detail-label">Max Users</span><span className="detail-value">{org.settings.maxUsers}</span></div>
            <div className="setting-item"><span className="detail-label">Max Projects</span><span className="detail-value">{org.settings.maxProjects}</span></div>
            <div className="setting-item"><span className="detail-label">SSO Enabled</span><StatusPill status={org.settings.enableSSO ? 'active' : 'inactive'} label={org.settings.enableSSO ? 'Yes' : 'No'} /></div>
            <div className="setting-item"><span className="detail-label">Audit Logging</span><StatusPill status={org.settings.enableAuditLog ? 'active' : 'inactive'} label={org.settings.enableAuditLog ? 'Yes' : 'No'} /></div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default OrganizationDetail;
